<script>

import SustainabilityMixin from '@/components/mixins/SustainabilityMixin'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import C3Chart from '@/components/shared/chart/C3Chart.vue'
import router from '@/router'
import { cloneDeep, defaultsDeep, get, forEach, snakeCase, min, max, toNumber, debounce } from 'lodash'

export default {
  name: 'DieselStagingChart',
  extends: C3Chart,
  mixins: [CurrentUserMixin, SustainabilityMixin],
  props: {
    height: {
      type: Number,
      default: 400
    },
    type: {
      type: String,
      default: 'bar'
    },
    data: {
      type: Array,
      default: () => []
    },
    average: null
  },
  computed: {
    chartString () {
      return this.$t('sustainability_chart_labels.average_units_used_during_staging', { unit: this.$tc(this.dieselUnitLabel, 1) })
    }
  },
  data () {
    return {
      /**
       * Desired width of the chart in pixels
       *
       * @var int
       */
      width: 0,
      children: []
    }
  },
  methods: {
    regenerate: debounce(function regenerate () {
      this.$chart.destroy()
      this.$chart = this.$c3.generate({
        bindto: this.$refs.root,
        ...this.getArgs()
      })
    }, 700),
    /**
     * If the customer context changes then regenerate the chart in order to up
     * date axis lables, etc... (called by the CurrentUserMixin)
     *
     * @return undefined
     */
    customerContextChangeHandler () {
      this.regenerate()
    },

    /**
     * Generates a config with the correct color pattern, temperature format,
     * etc.. based on the props passed in
     *
     * @return array
     */
    getConfig () {
      const genericConfig = cloneDeep(this.config)

      const config = {
        legend: {
          hide: true,
          item: {
            onclick: (id) => {
              this.$chart.toggle(id)
            }
          }
        },
        x: 'x',
        axis: {
          x: {
            label: {
              text: this.$t('sustainability_chart_labels.dcs'),
              position: 'outer-center'
            },
            type: 'category',
            tick: {
              rotate: -50,
              multiline: false
            }
          },
          y: {
            label: {
              text: this.chartString,
              position: 'outer-middle',
              rotate: 45
            },
            tick: {
              values: [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100]
            },
            padding: {
              top: 30
            }
          }
        },
        grid: {
          x: {
            lines: []
          },
          y: {
            lines: [
              {
                value: this.average,
                text: this.average,
                class: 'avgline'
              }
            ]
          }
        },
        regions: [],
        point: {
          r: 0,
          select: {
            r: 2
          }
        },
        line: {
          connectNull: true
        },
        tooltip: {
          format: {
            value: function (value, ratio, id) {
              return `${value}`
            }
          }
        },
        size: {
          height: this.height
        },
        /**
         * I found that the chart would expand to fit the width of the parent
         * element but never resize back down when the window was narrowed. The
         * onresized and onrendered handlers below work together to resize the
         * chart into the correct width by setting the chart's width to 1px when
         * the window is resized (allowing the parent div to resize to it's
         * natural width) and then once rendered if the chart is 1px wide
         * re-size to the parent div's width
         */
        onresized: function () {
          this.handleResize()
        }.bind(this),

        onrendered: function () {
          this.handleRender()
        }.bind(this)
      }
      return defaultsDeep(config, genericConfig)
    },

    /**
     * Structures the props into data suitable for the c3 chart
     *
     * @return array
     */
    getData () {
      const { type } = this

      const chartData = []
      const xs = {}

      const data = {
        type: 'bar',
        xs: xs,
        x: 'x',
        columns: chartData,
        onclick: (d, element) => {
          const customerId = this.children[d.x] // Assuming this returns the customer ID
          router.push({ path: `/sustainability/staging?customer_id=${customerId}` })
        }
      }

      const chartDataItems = ['Average Used During Staging']
      const xItems = ['x']
      forEach(this.data, (value, key) => {
        if (value.parent_id != null) {
          this.children.push(value.id)
          chartDataItems.push(this.convertDieselFromLiters(value.metricData.diesel.power_diesel_average_l, 0))
          xItems.push(value.name)
        }
      })
      chartData.push(chartDataItems, xItems)
      return defaultsDeep({ type }, data)
    }
  },
  watch: {
    sustainabilityParams (newValue, oldValue) {
      if (
        newValue.dieselUnit !== oldValue.dieselUnit
      ) {
        this.regenerate()
      }
    }
  }
}
</script>

<style lang="scss">
    @import "src/assets/scss/colors.scss";
    .c3-ygrid-line.avgline text{
      font-size: 25px;
    }
</style>
